import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, CheckCircle } from 'lucide-react';

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revenueStats, setRevenueStats] = useState({
    monthly: 0,
    annual: 0,
    active: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // TODO: Replace with actual API calls
      // const [subsRes, tiersRes] = await Promise.all([
      //   adminSubscriptionsAPI.getAll(),
      //   adminSubscriptionsAPI.getTiers()
      // ]);
      
      // Placeholder data
      setTimeout(() => {
        setTiers([
          { id: 1, name: 'Casual Host', price_monthly: 19.99, games_per_month: 4 },
          { id: 2, name: 'Regular Host', price_monthly: 49.99, games_per_month: 12 },
          { id: 3, name: 'Pro Host', price_monthly: 99.99, games_per_month: -1 }
        ]);
        
        setSubscriptions([
          {
            id: 2,
            host_name: 'Test Host',
            host_email: 'test@quizzler.com',
            tier_name: 'Regular Host',
            price_monthly: 49.99,
            status: 'active',
            games_used: 0,
            games_limit: 12,
            billing_cycle_start: '2025-12-02',
            billing_cycle_end: '2026-01-02'
          }
        ]);
        
        setRevenueStats({
          monthly: 49.99,
          annual: 599.88,
          active: 1
        });
        
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading subscriptions...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Subscriptions</h1>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Monthly Revenue</h3>
            <DollarSign size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-green-600">${revenueStats.monthly}</p>
          <p className="text-sm text-gray-500 mt-2">Recurring</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Annual Revenue</h3>
            <TrendingUp size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-slate-600">${revenueStats.annual}</p>
          <p className="text-sm text-gray-500 mt-2">Projected</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Active Subs</h3>
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <p className="text-4xl font-bold text-azure-600">{revenueStats.active}</p>
          <p className="text-sm text-gray-500 mt-2">Paying hosts</p>
        </div>
      </div>

      {/* Subscription Tiers Overview */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Subscription Tiers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiers.map((tier) => (
            <div key={tier.id} className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-bold text-gray-800 text-lg mb-2">{tier.name}</h3>
              <p className="text-3xl font-bold text-slate-600 mb-2">
                ${tier.price_monthly}
                <span className="text-sm text-gray-500">/mo</span>
              </p>
              <p className="text-sm text-gray-600">
                {tier.games_per_month === -1 ? 'Unlimited' : tier.games_per_month} games/month
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Active Subscriptions Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Active Subscriptions</h2>
        </div>
        
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Host</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Plan</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Price</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Usage</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Next Billing</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {subscriptions.map((sub) => (
              <tr key={sub.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-semibold text-gray-900">{sub.host_name}</p>
                    <p className="text-sm text-gray-500">{sub.host_email}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{sub.tier_name}</td>
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                  ${sub.price_monthly}/mo
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-600">
                    {sub.games_used}/{sub.games_limit === -1 ? '∞' : sub.games_limit}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                    sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {new Date(sub.billing_cycle_end).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <button className="text-slate-600 hover:text-slate-700 font-semibold">
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
